import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
  ApplicationCapabilityConfiguration,
  ApplicationChannelConfiguration,
  ApplicationConfiguration,
  ApplicationConfigurationService,
  AutonomousPolicy, AutonomousIncident, AutonomousApproval,
  WhatsAppRoute, WhatsAppRouteNumber,
} from './application-configuration.service';

const CHANNELS = [
  { code: 'INTERNAL_CHATBOT', label: 'Internal Chatbot', icon: 'support_agent' },
  { code: 'PUBLIC_CHATBOT', label: 'Public Chatbot', icon: 'smart_toy' },
  { code: 'WHATSAPP', label: 'WhatsApp', icon: 'chat' },
] as const;
const EVENT_TYPES = ['DEVICE_DOWN','INTERFACE_DOWN','SERVICE_DOWN','CRITICAL_ALERT','HIGH_PACKET_LOSS','HIGH_LATENCY','HIGH_UTILIZATION','DEVICE_RECOVERY'];
const ACTIONS = ['TRIGGER_POLL','REDISCOVER_DEVICE','ACKNOWLEDGE_ALERT','RESET_DEMO_INTERFACE','RESTORE_DEMO_DEVICE','RESTART_DEMO_SERVICE'];
const IP_CAPABILITIES = [
 {code:'NMS_IP_VIEW',provider:'IP_VIEW',label:'IP Address Visibility'}, {code:'NMS_IP_SEARCH',provider:'IP_SEARCH',label:'IP Search'},
 {code:'NMS_IP_PING',provider:'IP_PING',label:'Ping / Reachability'}, {code:'NMS_IP_TRACE',provider:'IP_TRACE',label:'Trace Path'},
 {code:'NMS_IP_ROUTING',provider:'IP_ROUTING',label:'Routing Information'}, {code:'NMS_IP_ARP_MAC',provider:'IP_ARP_MAC',label:'ARP / MAC Lookup'},
 {code:'NMS_IP_BGP_OSPF',provider:'IP_BGP_OSPF',label:'BGP / OSPF Information'}];

@Component({
  selector: 'app-applications',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './applications.component.html',
  styleUrl: './applications.component.css',
})
export class ApplicationsComponent implements OnInit {
  readonly channels = CHANNELS;
  applications: ApplicationConfiguration[] = [];
  editing: ApplicationConfiguration | null = null;
  loading = true;
  saving = false;
  error = '';
  message = '';
  readonly eventTypes = EVENT_TYPES; readonly autonomousActions = ACTIONS;
  autonomousPolicy: AutonomousPolicy | null = null;
  activity: AutonomousIncident[] = []; approvals: AutonomousApproval[] = [];
  savingAutonomous = false;
  readonly ipCapabilities=IP_CAPABILITIES; providerSupport: Record<string,Record<string,boolean>>={};
  savingNmsCapabilities = false;
  private capabilitySaveTimer: ReturnType<typeof setTimeout> | null = null;
  whatsappRoutes: WhatsAppRoute[]=[]; selectedWhatsAppProvider=''; whatsappRouteNumbers:WhatsAppRouteNumber[]=[];
  editingWhatsAppRoute:WhatsAppRoute|null=null; editingWhatsAppRouteIsNew=false;
  editingWhatsappNumber:WhatsAppRouteNumber={name:'',role:'ERP_READER',whatsappNumber:'',active:true}; savingWhatsapp=false;

  constructor(private readonly configurationService: ApplicationConfigurationService) {}

  ngOnInit(): void { void this.load(); void this.loadAutonomous(); void this.loadProviderSupport(); void this.loadWhatsappRouting(); }
  async loadWhatsappRouting():Promise<void>{try{this.whatsappRoutes=await this.configurationService.whatsappRoutes();const selected=this.whatsappRoutes.find(x=>x.enabled)||this.whatsappRoutes[0];this.selectedWhatsAppProvider=selected?.providerCode||'';this.editingWhatsAppRoute=selected?{...selected,authSecret:''}:null;this.editingWhatsAppRouteIsNew=false;await this.loadWhatsappNumbers();}catch(e){this.error=this.errorMessage(e,'Unable to load WhatsApp routing.');}}
  async loadWhatsappNumbers():Promise<void>{this.whatsappRouteNumbers=this.selectedWhatsAppProvider?await this.configurationService.whatsappRouteNumbers(this.selectedWhatsAppProvider):[];}
  async changeWhatsappProvider(providerCode:string):Promise<void>{
    if(!providerCode||this.savingWhatsapp)return;
    const previous=this.whatsappRoutes.find(x=>x.enabled)?.providerCode||this.selectedWhatsAppProvider;
    this.selectedWhatsAppProvider=providerCode;
    this.savingWhatsapp=true;
    this.error='';
    try{
      const routes=await this.configurationService.activateWhatsAppRoute(providerCode);
      this.whatsappRoutes=routes;
      const selected=routes.find(x=>x.enabled)||routes.find(x=>x.providerCode===providerCode);
      this.selectedWhatsAppProvider=selected?.providerCode||providerCode;
      this.editingWhatsAppRoute=selected?{...selected,authSecret:''}:null;
      this.editingWhatsAppRouteIsNew=false;
      this.clearWhatsappNumber();
      await this.loadWhatsappNumbers();
      this.message=`WhatsApp routing now uses ${selected?.displayName||providerCode}.`;
    }catch(e){
      this.selectedWhatsAppProvider=previous;
      this.editSelectedWhatsAppRoute();
      this.error=this.errorMessage(e,'Unable to change WhatsApp routing.');
    }finally{this.savingWhatsapp=false;}
  }
  newWhatsAppRoute():void{this.editingWhatsAppRouteIsNew=true;this.editingWhatsAppRoute={providerCode:'',displayName:'',enabled:false,routeType:'HTTP',matchMode:'LOCAL_ALLOWLIST',eligibilityUrl:null,eligibilityResponseField:null,messageUrl:'',responseField:'reply',authType:'NONE',authUsername:null,authSecret:'',secretConfigured:false,timeoutMs:65000,failureMessage:'The selected application is temporarily unavailable. Please try again shortly.',failureMode:'ISOLATE',priority:100};}
  editSelectedWhatsAppRoute():void{const selected=this.whatsappRoutes.find(x=>x.providerCode===this.selectedWhatsAppProvider);this.editingWhatsAppRoute=selected?{...selected,authSecret:''}:null;this.editingWhatsAppRouteIsNew=false;}
  async saveWhatsAppRoute():Promise<void>{const route=this.editingWhatsAppRoute;if(!route?.providerCode.trim()||!route.displayName.trim()||(route.routeType==='HTTP'&&!route.messageUrl?.trim()))return;this.savingWhatsapp=true;try{const wasNew=this.editingWhatsAppRouteIsNew;const saved=await this.configurationService.saveWhatsAppRoute(route,wasNew);this.whatsappRoutes=wasNew?await this.configurationService.activateWhatsAppRoute(saved.providerCode):await this.configurationService.whatsappRoutes();this.selectedWhatsAppProvider=saved.providerCode;const current=this.whatsappRoutes.find(x=>x.providerCode===saved.providerCode)||saved;this.editingWhatsAppRoute={...current,authSecret:''};this.editingWhatsAppRouteIsNew=false;await this.loadWhatsappNumbers();this.message=`${saved.displayName} route saved. Changes are live.`;}catch(e){this.error=this.errorMessage(e,'Unable to save WhatsApp route.');}finally{this.savingWhatsapp=false;}}
  async removeWhatsAppRoute():Promise<void>{const route=this.editingWhatsAppRoute;if(!route||this.editingWhatsAppRouteIsNew||route.enabled||!confirm(`Delete ${route.displayName}?`))return;this.savingWhatsapp=true;try{await this.configurationService.deleteWhatsAppRoute(route.providerCode);await this.loadWhatsappRouting();this.message='WhatsApp route deleted.';}catch(e){this.error=this.errorMessage(e,'Unable to delete WhatsApp route.');}finally{this.savingWhatsapp=false;}}
  editWhatsappNumber(value:WhatsAppRouteNumber):void{this.editingWhatsappNumber={...value};}
  clearWhatsappNumber():void{this.editingWhatsappNumber={name:'',role:'ERP_READER',whatsappNumber:'',active:true};}
  async saveWhatsappNumber():Promise<void>{if(!this.selectedWhatsAppProvider||!this.editingWhatsappNumber.name.trim()||!this.editingWhatsappNumber.whatsappNumber.trim())return;this.savingWhatsapp=true;try{await this.configurationService.saveWhatsAppRouteNumber(this.selectedWhatsAppProvider,this.editingWhatsappNumber);this.clearWhatsappNumber();await this.loadWhatsappNumbers();this.message='WhatsApp route number saved. Changes are live.';}catch(e){this.error=this.errorMessage(e,'Unable to save WhatsApp route number.');}finally{this.savingWhatsapp=false;}}
  async removeWhatsappNumber(value:WhatsAppRouteNumber):Promise<void>{if(!value.id||!this.selectedWhatsAppProvider||!confirm(`Delete ${value.name}?`))return;await this.configurationService.deleteWhatsAppRouteNumber(this.selectedWhatsAppProvider,value.id);await this.loadWhatsappNumbers();}
  async loadProviderSupport():Promise<void>{try{this.providerSupport=await this.configurationService.providerCapabilities();}catch{this.providerSupport={};}}
  providerSupported(code:string):boolean{return Object.values(this.providerSupport).some(c=>c[code]===true);}
  get nmsApplication(): ApplicationConfiguration | undefined { return this.applications.find(app => app.applicationCode.toUpperCase() === 'NMS'); }
  capability(application:ApplicationConfiguration,code:string):ApplicationCapabilityConfiguration|undefined{return application.capabilities.find(x=>x.capabilityCode===code);}
  setCapability(code:string,label:string,enabled:boolean):void{if(!this.editing)return;let c=this.capability(this.editing,code);if(c)c.enabled=enabled;else this.editing.capabilities.push({capabilityCode:code,capabilityName:label,enabled,displayOrder:200+this.editing.capabilities.length});}
  setNmsCapability(code:string,label:string,enabled:boolean):void{const app=this.nmsApplication;if(!app)return;let c=this.capability(app,code);if(c)c.enabled=enabled;else app.capabilities.push({capabilityCode:code,capabilityName:label,enabled,displayOrder:200+app.capabilities.length});}
  updateNmsCapability(code:string,label:string,enabled:boolean):void{this.setNmsCapability(code,label,enabled);if(this.capabilitySaveTimer)clearTimeout(this.capabilitySaveTimer);this.capabilitySaveTimer=setTimeout(()=>{this.capabilitySaveTimer=null;void this.saveNmsCapabilities();},350);}
  async saveNmsCapabilities():Promise<void>{const app=this.nmsApplication;if(!app)return;this.savingNmsCapabilities=true;this.error='';try{const saved=await this.configurationService.update(this.normalize(structuredClone(app)));const index=this.applications.findIndex(item=>item.applicationId===saved.applicationId);if(index>=0)this.applications[index]=saved;this.message='NMS advanced network capabilities saved.';}catch(e){this.error=this.errorMessage(e,'Unable to save NMS capabilities.');}finally{this.savingNmsCapabilities=false;}}

  async loadAutonomous(): Promise<void> {
    try { const [p,a,q]=await Promise.all([this.configurationService.autonomousPolicy(),this.configurationService.autonomousActivity(),this.configurationService.autonomousApprovals()]);this.autonomousPolicy=p;this.activity=a;this.approvals=q; } catch { /* migration may not be deployed yet */ }
  }
  toggleSet(field: 'severities'|'eventTypes'|'requiresApproval', value: string, enabled: boolean): void { if(!this.autonomousPolicy)return;const values=this.autonomousPolicy[field];this.autonomousPolicy[field]=enabled?[...new Set([...values,value])]:values.filter(x=>x!==value); }
  has(field: 'severities'|'eventTypes'|'requiresApproval', value: string): boolean { return this.autonomousPolicy?.[field].includes(value) ?? false; }
  async saveAutonomous(): Promise<void> { if(!this.autonomousPolicy)return;this.savingAutonomous=true;try{this.autonomousPolicy=await this.configurationService.saveAutonomousPolicy(this.autonomousPolicy);this.message='NMS autonomous policy saved.';}catch(e){this.error=this.errorMessage(e,'Unable to save autonomous policy.');}finally{this.savingAutonomous=false;} }
  async decide(item: AutonomousApproval, decision: 'APPROVED'|'REJECTED'): Promise<void> { await this.configurationService.decideApproval(item.id,decision);await this.loadAutonomous(); }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      this.applications = await this.configurationService.list();
    } catch (error) {
      this.error = this.errorMessage(error, 'Unable to load application configuration.');
    } finally {
      this.loading = false;
    }
  }

  edit(application: ApplicationConfiguration): void {
    this.editing = this.normalize(structuredClone(application));
    this.message = '';
    this.error = '';
  }

  addApplication(): void {
    this.editing = this.normalize({
      applicationName: '', applicationCode: '', enabled: false,
      integrationType: 'NONE', integrationProfile: '', channels: [], capabilities: [],
    });
    this.message = '';
    this.error = '';
  }

  cancel(): void { this.editing = null; }

  async save(): Promise<void> {
    if (!this.editing || !this.editing.applicationName.trim() || !this.editing.applicationCode.trim()) return;
    this.saving = true;
    this.error = '';
    try {
      const payload = this.normalize(structuredClone(this.editing));
      payload.applicationCode = payload.applicationCode.trim().toUpperCase();
      payload.integrationType = payload.integrationType?.trim().toUpperCase() || null;
      const saved = payload.applicationId
        ? await this.configurationService.update(payload)
        : await this.configurationService.create(payload);
      const index = this.applications.findIndex(item => item.applicationId === saved.applicationId);
      if (index >= 0) this.applications[index] = saved;
      else this.applications = [...this.applications, saved];
      this.applications.sort((a, b) => a.applicationName.localeCompare(b.applicationName));
      this.editing = null;
      this.message = `${saved.applicationName} configuration saved.`;
    } catch (error) {
      this.error = this.errorMessage(error, 'Unable to save application configuration.');
    } finally {
      this.saving = false;
    }
  }

  channel(application: ApplicationConfiguration, code: string): ApplicationChannelConfiguration {
    return application.channels.find(item => item.channelCode === code) ?? { channelCode: code, enabled: false };
  }

  setChannel(code: string, enabled: boolean): void {
    if (!this.editing) return;
    const channel = this.editing.channels.find(item => item.channelCode === code);
    if (channel) channel.enabled = enabled;
    else this.editing.channels.push({ channelCode: code, enabled });
  }

  addCapability(): void {
    if (!this.editing) return;
    this.editing.capabilities.push({
      capabilityCode: '', capabilityName: '', enabled: true,
      displayOrder: this.editing.capabilities.length + 1,
    });
  }

  removeCapability(index: number): void { this.editing?.capabilities.splice(index, 1); }

  trackByApplication(_: number, item: ApplicationConfiguration): number | string {
    return item.applicationId ?? item.applicationCode;
  }

  trackByWhatsAppRoute(_:number,item:WhatsAppRoute):string{return item.providerCode;}

  private normalize(application: ApplicationConfiguration): ApplicationConfiguration {
    application.channels = CHANNELS.map(channel => ({
      ...(application.channels ?? []).find(item => item.channelCode === channel.code),
      channelCode: channel.code,
      enabled: (application.channels ?? []).find(item => item.channelCode === channel.code)?.enabled ?? false,
    }));
    application.capabilities = application.capabilities ?? [];
    return application;
  }

  private errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }
}
